import React, { FC, useState } from 'react';
import { Button, Card, message } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { getPrimaryColor, nullSafeGet, nullSafeGetWithZero } from '../../utils/DataAccessUtils';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import {
	provisionAssetsFromBrandAssetGroupForBuyer,
	getProvisionAssetsFromBrandAssetGroupStatusForBuyer,
	provisionAssetsFromBrandAssetGroupForSupplier,
	getProvisionAssetsFromBrandAssetGroupStatusForSupplier,
} from '../../thunks/locations_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import OWPoll from '../common/OWPoll';
import { withUserType } from '../withUserType';

interface CreateAssetsFromBrandAssetGroupCardProps {
	currentLocation: any;
	history: any;
	location: any;
	userType: any;
	provisionAssets: (locationId: string) => Promise<any>;
	getProvisionStatus: (locationId: string) => Promise<any>;
	onRefreshLocation?: () => any;
}

const CreateAssetsFromBrandAssetGroupCard: FC<CreateAssetsFromBrandAssetGroupCardProps> = ({
	currentLocation,
	history,
	location,
	provisionAssets,
	getProvisionStatus,
	onRefreshLocation,
	userType,
}) => {
	const locationId = nullSafeGet('id', currentLocation);
	const brandAssetGroupName = nullSafeGet('brandAssetGroup.name', currentLocation);
	const brandId = nullSafeGet('brandAssetGroup.brandId', currentLocation);
	const allAssetCount = nullSafeGetWithZero('allAssetCount', currentLocation);

	const [initiated, setInitiated] = useState(false);
	const [creating, setCreating] = useState(false);

	const primaryColor = getPrimaryColor(userType);

	if (!brandAssetGroupName || allAssetCount !== 0) {
		return null;
	}

	const fetchStatus = () => getProvisionStatus(locationId);

	const handleCreateAssets = () => {
		setCreating(true);
		provisionAssets(locationId)
			.then(() => setInitiated(true))
			.catch((errorMessage) =>
				message.error(errorMessage || 'Unable to create assets. Please contact support.')
			)
			.finally(() => setCreating(false));
	};

	const navigateToAssets = () => {
		onRefreshLocation && onRefreshLocation();
		history.push(`/${userType}/assets/overview/all?locationIds=${locationId}`);
	};

	const navigateToBrand = () => {
		if (brandId) {
			const path = `/${userType}/locations/overview/brands/detail/${brandId}/details`;
			history.push(getLinkWIthBackLinkParams(location, 'Back to Location', path));
		}
	};

	return (
		<div className={initiated ? 'hidden' : 'rowSpacing'}>
			<Card
				bodyStyle={{
					backgroundColor: `color-mix(in srgb, ${primaryColor} 10%, white)`,
					borderRadius: 4,
				}}
			>
				<div className="text-base font-medium">Create assets?</div>
				<div className="mt-2">
					This location has a linked asset group{' '}
					<span
						className="cursor-pointer font-medium"
						style={{ color: primaryColor }}
						onClick={navigateToBrand}
					>
						{brandAssetGroupName}
					</span>
					. Should we use it to create assets?
				</div>
				<div className="mt-4">
					<OWPoll
						id={`create-assets-${locationId}`}
						fetchStatusPromises={[fetchStatus]}
						message="Creating assets from asset group..."
						successMessage="Assets created successfully!"
						successButtonText="Show Assets"
						onRefresh={navigateToAssets}
						successMessageMandatory={initiated}
						renderElementWithProps={({ disabled }) => (
							<Button
								type="primary"
								loading={creating || (disabled && !initiated)}
								disabled={disabled || creating}
								onClick={handleCreateAssets}
							>
								{disabled && !initiated && !creating ? 'CHECKING STATUS…' : 'CREATE ASSETS'}
							</Button>
						)}
					/>
				</div>
			</Card>
		</div>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => {
	const isSupplier = ownProps.userType === ROLE_TYPES.SUPPLIER;
	const provisionThunk = isSupplier
		? provisionAssetsFromBrandAssetGroupForSupplier
		: provisionAssetsFromBrandAssetGroupForBuyer;
	const statusThunk = isSupplier
		? getProvisionAssetsFromBrandAssetGroupStatusForSupplier
		: getProvisionAssetsFromBrandAssetGroupStatusForBuyer;
	return {
		provisionAssets: (locationId) => dispatch(provisionThunk(locationId)),
		getProvisionStatus: (locationId) => dispatch(statusThunk(locationId)),
	};
};

export default withUserType(
	withRouter(connect(null, mapDispatchToProps)(CreateAssetsFromBrandAssetGroupCard) as any) as any
);
